// src/api/quiz.ts
import { API_BASE, getAuthHeaders } from './config';

export type QuizLanguageCode = 'PL' | 'EN' | 'ES' | 'DE' | 'UK' | 'IT' | 'FR';
/** ---------- TYPY ---------- */

export interface QuizQuestionInput {
    term: string;                       // słówko z tekstu (np. "apple")
    correct_answer: string;             // poprawne tłumaczenie (np. "jabłko")
    wrong_answers: [string, string, string]; // dokładnie 3 błędne odpowiedzi
    order_index?: number;               // opcjonalnie kolejność (backend może nadawać sam)
}

export interface QuizInput {
    language_code: QuizLanguageCode;    // język quizu (taki jak tłumaczenia treści)
    title: string;                      // np. "Quiz ze słówek"
    description?: string | null;        // opcjonalny opis
    is_active?: boolean;                // czy quiz jest aktywny (domyślnie true po stronie backendu)
    questions: QuizQuestionInput[];     // lista pytań
}

export interface QuizBulkInput {
    items: QuizInput[];
}

export const importQuizzesForContent = async (
    contentId: string | number,
    payload: QuizBulkInput,
): Promise<{
    ok: boolean;
    imported_count: number;
    quizzes: Array<{
        language_code: QuizLanguageCode;
        quiz: QuizDTO;
        questions_count: number;
    }>;
}> => {
    const res = await fetch(`${API_BASE}/contents/${contentId}/quizzes/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw await res.json();
    return res.json();
};

/** Struktura odpowiedzi z GET/POST (przykładowa, dopasuj do backendu jeśli trzeba) */
export interface QuizQuestionDTO {
    id: number;
    quiz_id: number;
    term: string;
    correct_answer: string;
    wrong_answers: string[];
    order_index: number;
}

export interface QuizDTO {
    id: number;
    content_id: number;
    language_code: QuizLanguageCode;
    title: string;
    description: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    questions: QuizQuestionDTO[];
}

/** ---------- API: QUIZ DLA TREŚCI ---------- */

/**
 * Pobierz quiz dla treści.
 * Jeśli podasz lang, backend może zwrócić quiz dla konkretnego języka (np. EN).
 * Jeśli lang pominięte – logika po stronie backendu (np. domyślny lub lista).
 */
export const getQuizForContent = async (
    contentId: string | number,
    lang?: QuizLanguageCode,
): Promise<QuizDTO | null> => {
    const url = `${API_BASE}/contents/${contentId}/quiz${lang ? `?lang=${encodeURIComponent(lang)}` : ''
        }`;

    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) throw await res.json();
    return res.json();
};

/**
 * Utwórz / zaktualizuj quiz dla danej treści.
 * Backend może traktować to jako "upsert" (insert albo update po content_id + language_code).
 */
export const upsertQuizForContent = async (
    contentId: string | number,
    payload: QuizInput,
): Promise<QuizDTO> => {
    const res = await fetch(`${API_BASE}/contents/${contentId}/quiz`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw await res.json();
    return res.json();
};

/**
 * Usuń quiz dla danej treści.
 * Jeśli masz wersje językowe quizów, możesz przekazać lang, żeby usunąć tylko konkretny.
 */
export const deleteQuizForContent = async (
    contentId: string | number,
    lang?: QuizLanguageCode,
) => {
    const url = `${API_BASE}/contents/${contentId}/quiz${lang ? `?lang=${encodeURIComponent(lang)}` : ''
        }`;

    const res = await fetch(url, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    if (!res.ok) throw await res.json();
    return res.json();
};
